import { useState, useEffect } from 'react';
import { getProfilo } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Building2, Mail, Phone, MapPin, Globe, QrCode, FileText, Euro, Clock, CheckCircle, AlertCircle } from 'lucide-react';

const ProfiloPage = () => {
  const [profilo, setProfilo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProfilo();
  }, []);

  const loadProfilo = async () => {
    try {
      const response = await getProfilo();
      setProfilo(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento profilo');
    } finally {
      setLoading(false);
    }
  };

  const getStatoBadge = (stato) => {
    switch (stato) {
      case 'paid':
        return <Badge className="bg-green-100 text-green-700">Pagato</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700">In attesa</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-700">Annullato</Badge>;
      default:
        return <Badge variant="secondary">{stato}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!profilo) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-slate-400 mx-auto mb-4" />
        <p className="text-slate-500">Profilo non disponibile</p>
      </div>
    );
  }

  return (
    <div data-testid="profilo-page">
      {/* Header */}
      <div className="mb-6 lg:mb-8">
        <h1 
          className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-slate-900 tracking-tight"
          style={{ fontFamily: 'Playfair Display, serif' }}
        >
          Il tuo Profilo
        </h1>
        <p className="text-slate-500 mt-1 text-sm sm:text-base">Gestisci i dati della tua azienda</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        {/* Dati Azienda */}
        <div className="lg:col-span-2 space-y-4 lg:space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-slate-600" />
                Dati Azienda
              </CardTitle>
              <CardDescription>Informazioni della tua attività</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-500">Ragione Sociale</label>
                  <p className="font-medium text-slate-900">{profilo.ragione_sociale}</p>
                </div>
                <div>
                  <label className="text-sm text-slate-500">Tipo Account</label>
                  <p>
                    {profilo.tipo_account === 'premium' ? (
                      <Badge className="bg-purple-100 text-purple-700">Premium</Badge>
                    ) : (
                      <Badge variant="secondary">Base</Badge>
                    )}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-slate-500 flex items-center gap-1">
                    <Mail className="h-3 w-3" /> Email
                  </label>
                  <p className="font-medium text-slate-900">{profilo.email}</p>
                </div>
                <div>
                  <label className="text-sm text-slate-500 flex items-center gap-1">
                    <Phone className="h-3 w-3" /> Telefono
                  </label>
                  <p className="font-medium text-slate-900">{profilo.telefono || '-'}</p>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-sm text-slate-500 flex items-center gap-1">
                    <MapPin className="h-3 w-3" /> Indirizzo
                  </label>
                  <p className="font-medium text-slate-900">{profilo.indirizzo || '-'}</p>
                </div>
                {profilo.sito_web && (
                  <div>
                    <label className="text-sm text-slate-500 flex items-center gap-1">
                      <Globe className="h-3 w-3" /> Sito Web
                    </label>
                    <a href={profilo.sito_web} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      {profilo.sito_web}
                    </a>
                  </div>
                )}
                {profilo.pec && (
                  <div>
                    <label className="text-sm text-slate-500">PEC</label>
                    <p className="font-medium text-slate-900">{profilo.pec}</p>
                  </div>
                )}
                {profilo.sdi && (
                  <div>
                    <label className="text-sm text-slate-500">Codice SDI</label>
                    <p className="font-medium text-slate-900 font-mono">{profilo.sdi}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Storico Ordini */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-slate-600" />
                Storico Ordini
              </CardTitle>
              <CardDescription>I tuoi acquisti e attivazioni</CardDescription>
            </CardHeader>
            <CardContent>
              {profilo.ordini && profilo.ordini.length > 0 ? (
                <div className="space-y-3">
                  {profilo.ordini.map((ordine) => (
                    <div 
                      key={ordine.id} 
                      className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-slate-50 rounded-lg gap-2"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">
                          {ordine.nome_elogio || ordine.id_elogio}
                        </p>
                        <p className="text-xs text-slate-500">
                          {new Date(ordine.data_creazione).toLocaleDateString('it-IT')}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-slate-900">€{ordine.importo}</span>
                        {getStatoBadge(ordine.stato)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">Nessun ordine effettuato</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar Stats */}
        <div className="space-y-4 lg:space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Riepilogo QR Code</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-slate-600">
                  <QrCode className="h-4 w-4" />
                  <span>Totali</span>
                </div>
                <span className="text-2xl font-bold text-slate-900">{profilo.qr_totali}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="h-4 w-4" />
                  <span>Attivi</span>
                </div>
                <span className="text-2xl font-bold text-green-600">{profilo.qr_attivi}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-amber-600">
                  <Clock className="h-4 w-4" />
                  <span>Disponibili</span>
                </div>
                <span className="text-2xl font-bold text-amber-600">{profilo.qr_disponibili}</span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 text-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Euro className="h-5 w-5 text-sky-400" />
                <span className="text-sm text-slate-300">Tariffa Attivazione</span>
              </div>
              <p className="text-3xl font-bold">
                €{profilo.tipo_account === 'premium' ? '100' : '150'}
              </p>
              <p className="text-xs text-slate-400 mt-1">
                Account {profilo.tipo_account === 'premium' ? 'Premium' : 'Base'}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ProfiloPage;
